import React from 'react';

interface StepSummary {
  id: string;
  label: string;
  status: 'complete' | 'skipped' | 'active' | 'pending' | 'failed';
}

interface ResumePromptProps {
  projectName: string;
  steps: StepSummary[];
  currentStepIndex: number;
  totalSteps: number;
  onContinue: () => void;
  onStartOver: () => void;
}

/**
 * W-7: Visual resume prompt for the bootstrap wizard.
 *
 * Shown when a user returns to an incomplete wizard session.
 * Displays a summary of completed, current, and remaining steps.
 * Offers [Continue Setup] and [Start Over] actions.
 */
export const ResumePrompt: React.FC<ResumePromptProps> = ({
  projectName,
  steps,
  currentStepIndex,
  totalSteps,
  onContinue,
  onStartOver,
}) => {
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'rgba(0, 0, 0, 0.6)', backdropFilter: 'blur(4px)',
    }}>
      <div style={{
        background: 'var(--surface)', border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg, 12px)', padding: '28px 32px',
        maxWidth: 440, width: '100%', boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
      }}>
        {/* Header */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 'var(--text-lg, 18px)', fontWeight: 600, color: 'var(--text)', marginBottom: 6 }}>
            Welcome back
          </div>
          <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
            You were setting up <strong style={{ color: 'var(--text)' }}>{projectName}</strong>.
            Here's where you left off:
          </div>
        </div>

        {/* Step list */}
        <div style={{
          display: 'flex', flexDirection: 'column', gap: 2,
          padding: '12px 0', borderTop: '1px solid var(--border)',
          borderBottom: '1px solid var(--border)', marginBottom: 20,
        }}>
          {steps.map((step, i) => {
            const icon = step.status === 'complete' || step.status === 'skipped'
              ? '✅'
              : step.status === 'active'
                ? '⏳'
                : step.status === 'failed'
                  ? '❌'
                  : '○';

            const labelColor = step.status === 'complete' || step.status === 'skipped'
              ? 'var(--text-muted)'
              : step.status === 'active'
                ? 'var(--text)'
                : step.status === 'failed'
                  ? 'var(--red)'
                  : 'var(--text-muted)';

            const rightText = step.status === 'complete'
              ? 'Connected'
              : step.status === 'skipped'
                ? 'Skipped'
                : step.status === 'active'
                  ? `Step ${currentStepIndex + 1} of ${totalSteps}`
                  : step.status === 'failed'
                    ? 'Failed'
                    : '';

            return (
              <div
                key={step.id}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '6px 8px', borderRadius: 'var(--radius-sm, 4px)',
                  background: step.status === 'active' ? 'var(--surface2, rgba(255,255,255,0.05))' : 'transparent',
                }}
              >
                <span style={{ fontSize: 14, width: 20, textAlign: 'center', flexShrink: 0 }}>
                  {icon}
                </span>
                <span style={{
                  fontSize: 'var(--text-sm)', color: labelColor,
                  fontWeight: step.status === 'active' ? 600 : 400,
                  flex: 1,
                }}>
                  {step.label}
                </span>
                {rightText && (
                  <span style={{
                    fontSize: 'var(--text-xs)', color: 'var(--text-muted)',
                    fontFamily: 'var(--font-mono, monospace)',
                  }}>
                    {rightText}
                  </span>
                )}
              </div>
            );
          })}
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            onClick={onContinue}
            style={{
              flex: 1, padding: '10px 16px',
              background: 'var(--orange, #00E5FF)', color: 'white',
              border: 'none', borderRadius: 'var(--radius-md, 8px)',
              fontSize: 'var(--text-sm)', fontWeight: 600, cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            Continue Setup
          </button>
          <button
            onClick={onStartOver}
            style={{
              flex: 1, padding: '10px 16px',
              background: 'transparent', color: 'var(--text-muted)',
              border: '1px solid var(--border)', borderRadius: 'var(--radius-md, 8px)',
              fontSize: 'var(--text-sm)', fontWeight: 500, cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            Start Over
          </button>
        </div>
      </div>
    </div>
  );
};
